/* eslint-disable no-unneeded-ternary */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { Box, Menu, MenuItem, Chip, Tooltip, Popover } from '@mui/material';
import { Iconify } from 'src/components';
import { followUpSlice, updateTreatmentDetail } from '../../../../redux/slices/lead';
import { dispatch, useSelector } from '../../../../redux/store';
import {
    TabCount,
    TablePageNumber,
    TablePerPage,
    TableSearch,
    TableTreatment,
    TableFilterDate,
    TableStatus,
    TableFilterTags,
    TableOrder,
    TableOrderBy,
    TableReferral,
    TableCategory,
    TableBookedWith
} from '../../../../utils/allTableTab';

const formatUpdatedDate = (raw) => {
    if (!raw) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
    const cleaned = raw.replace(/\.\d+Z?$/, '').trim();
    const parsed = moment(cleaned, 'DD-MM-YYYY HH:mm:ss');
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : raw;
};

// Module-level cache: treatmentId → latest attributes (optimistic + server-confirmed).
// Shared across all ChoicesField instances in the same row so that saving col2 doesn't
// send stale data that overwrites col1's already-saved value.
const treatmentAttrsCache = new Map();

const CHIP_COLORS = {
    yes: { bgcolor: '#16A34A', color: '#fff', icon: 'eva:checkmark-circle-2-fill' },
    no: { bgcolor: '#DC2626', color: '#fff', icon: 'eva:close-circle-fill' },
    skip: { bgcolor: '#F59E0B', color: '#fff', icon: 'eva:minus-circle-fill' },
    skipped: { bgcolor: '#F59E0B', color: '#fff', icon: 'eva:minus-circle-fill' },
    paused: { bgcolor: '#F59E0B', color: '#fff', icon: 'eva:pause-circle-fill' },
    arrived: { bgcolor: '#2563EB', color: '#fff', icon: 'eva:checkmark-circle-2-fill' },
};
const DEFAULT_CHIP = { bgcolor: '#E0E7FF', color: '#4338CA', icon: 'eva:radio-button-on-fill' };
const EMPTY_CHIP = { bgcolor: '#F3F4F6', color: '#9CA3AF', icon: null };

const getChipStyle = (val) => {
    if (!val || val.trim() === '') return EMPTY_CHIP;
    return CHIP_COLORS[val.toLowerCase()] || DEFAULT_CHIP;
};

const ChoicesField = ({ rowData, tableType, handleSetTableData, choiceValue, updateLeadPage, reloadCounter, choicesData }) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [value, setValue] = useState('');
    const [dateValue, setDateValue] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const perPage = TablePerPage(tableType);
    const pageNumber = TablePageNumber(tableType);
    const status = TableStatus(tableType);
    const tableOrder = TableOrder(tableType);
    const tableOrderBy = TableOrderBy(tableType);
    const searchValue = TableSearch(tableType);
    const treatmentValue = TableTreatment(tableType);
    const searchFilterDate = TableFilterDate(tableType);
    const startDate = Array.isArray(searchFilterDate) ? searchFilterDate[0]?.startDate : null;
    const endDate = Array.isArray(searchFilterDate) ? searchFilterDate[0]?.endDate : null;
    const contactCount = TabCount(tableType);
    const searchTagsValue = TableFilterTags(tableType)?.toString();
    const referral = TableReferral(tableType);
    const category = TableCategory(tableType);
    const bookedWith = TableBookedWith(tableType);
    const treatmentData = [];
    treatmentValue?.map((val) => treatmentData.push(val.id));

    // Menu/popover state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [dateAnchor, setDateAnchor] = useState(null);
    const [saved, setSaved] = useState(false);

    // Tracks the value we optimistically submitted. While set, rowData-driven UI resets
    // are blocked. Clears once rowData confirms the saved value.
    const pendingValueRef = useRef(null);

    const treatmentId = rowData?.treatmentData?.[0]?.id;

    // Returns the latest known attributes for this row's treatment.
    // Uses the shared module-level cache so all columns see each other's pending saves.
    const getLatestAttrs = () => {
        if (treatmentId && treatmentAttrsCache.has(treatmentId)) {
            return treatmentAttrsCache.get(treatmentId);
        }
        return rowData?.leadTreatmentAttributes || [];
    };

    useEffect(() => {
        if (!rowData || !choiceValue) return;
        const serverValue = rowData?.leadTreatmentAttributes?.find(el => el.key === choiceValue?.name)?.data?.[0]?.value || '';

        if (pendingValueRef.current !== null) {
            // Save is in flight — only accept rowData once it reflects our submitted value
            if (serverValue === pendingValueRef.current) {
                pendingValueRef.current = null;
                // Server confirmed — refresh shared cache with authoritative server data
                if (treatmentId) treatmentAttrsCache.set(treatmentId, rowData.leadTreatmentAttributes || []);
            }
            return;
        }

        // No pending save — sync UI and cache from server data
        if (treatmentId) treatmentAttrsCache.set(treatmentId, rowData.leadTreatmentAttributes || []);
        const dateString = serverValue;
        const initialDate = dateString ? moment(dateString, 'DD/MM/YYYY').toDate() : null;
        setValue(serverValue);
        setDateValue(initialDate);
    }, [rowData, choiceValue]);

    const updateAttributeData = (currentData, newValue) => {
        const latestCycle = Math.max(...currentData.map(d => d.cycle), 0);
        const existingCycleIndex = currentData.findIndex(d => d.cycle === latestCycle);
        const updatedAt = moment().format('DD/MM/YYYY');
        if (existingCycleIndex !== -1) {
            currentData[existingCycleIndex] = { ...currentData[existingCycleIndex], value: newValue, updated_at: updatedAt };
        } else {
            currentData.push({ value: newValue, cycle: latestCycle + 1, updated_at: updatedAt });
        }
        return currentData;
    };

    const handleChange = (event) => {
        const newValue = event.target.value;
        pendingValueRef.current = newValue;
        const approvalStatusKey = 'Approval Status';
        const approvedDateKey = 'Approved Date';
        const updatedAt = moment().format('DD/MM/YYYY');
        let updatedAttributes = [...getLatestAttrs()];
        choicesData?.forEach((choice) => {
            const existingAttributeIndex = updatedAttributes.findIndex(attr => attr.key === choice.name);
            if (choice.name === choiceValue.name) {
                if (newValue === '') {
                    if (existingAttributeIndex !== -1) {
                        updatedAttributes = updatedAttributes.filter((_, index) => index !== existingAttributeIndex);
                    }
                } else if (existingAttributeIndex !== -1) {
                    const currentData = [...updatedAttributes[existingAttributeIndex].data];
                    const updatedData = updateAttributeData(currentData, newValue);
                    updatedAttributes[existingAttributeIndex] = { ...updatedAttributes[existingAttributeIndex], data: updatedData };
                } else {
                    updatedAttributes.push({
                        key: choice.name,
                        data: [{ value: newValue, cycle: 1, updated_at: updatedAt }],
                    });
                }
            } else if (existingAttributeIndex === -1) {
                updatedAttributes.push({
                    key: choice.name,
                    data: [{ value: '', cycle: 1 }],
                });
            }
        });
        if (choiceValue?.name?.toLowerCase().includes(approvalStatusKey.toLowerCase()) && newValue === 'Arrived') {
            const approvedDateIndex = updatedAttributes.findIndex(el => el.key === approvedDateKey);
            const currentDateString = moment().format('DD-MM-YYYY');
            if (approvedDateIndex !== -1) {
                const currentData = [...updatedAttributes[approvedDateIndex].data];
                const updatedData = updateAttributeData(currentData, currentDateString);
                updatedAttributes[approvedDateIndex] = { ...updatedAttributes[approvedDateIndex], data: updatedData };
            } else {
                updatedAttributes.push({
                    key: approvedDateKey,
                    data: [{ value: currentDateString, cycle: 1 }],
                });
            }
        }
        // Update shared cache immediately so sibling columns read our value before the PATCH returns
        if (treatmentId) {
            treatmentAttrsCache.set(treatmentId, updatedAttributes);
        }
        const detail = {
            lead_treatment_attributes: updatedAttributes
        };
        dispatch(updateTreatmentDetail(practiceId, rowData?.lead_id, rowData?.treatmentData?.[0]?.id, detail, handleSuccess, 'choice_cell'));
        setValue(newValue);
    };

    const onChangeDate = (newValue) => {
        const newValueWithDate = newValue ? moment(newValue, 'DD/MM/YYYY').format('DD-MM-YYYY') : '';
        pendingValueRef.current = newValueWithDate;
        const updatedAt = moment().format('DD/MM/YYYY');
        // Use shared cache for the same reason as handleChange
        const updatedAttributes = [...getLatestAttrs()];
        choicesData?.forEach((choice) => {
            const existingAttributeIndex = updatedAttributes.findIndex(attr => attr.key === choice.name);
            if (choice.name === choiceValue.name) {
                if (existingAttributeIndex !== -1) {
                    const currentData = [...updatedAttributes[existingAttributeIndex].data];
                    const updatedData = updateAttributeData(currentData, newValueWithDate);
                    updatedAttributes[existingAttributeIndex] = {
                        ...updatedAttributes[existingAttributeIndex],
                        data: updatedData
                    };
                } else {
                    updatedAttributes.push({
                        key: choice.name,
                        data: [{ value: newValueWithDate, cycle: 1, updated_at: updatedAt }],
                    });
                }
            } else if (existingAttributeIndex === -1) {
                updatedAttributes.push({
                    key: choice.name,
                    data: [{ value: '', cycle: 1 }],
                });
            }
        });
        if (treatmentId) treatmentAttrsCache.set(treatmentId, updatedAttributes);
        const detail = {
            lead_treatment_attributes: updatedAttributes
        };
        dispatch(updateTreatmentDetail(practiceId, rowData?.lead_id, rowData?.treatmentData?.[0]?.id, detail, handleSuccess, 'choice_cell'));
        setDateValue(newValue);
        setDateAnchor(null);
    };

    const handleSuccess = (data) => {
        setSaved(true);
        setTimeout(() => setSaved(false), 400);
        enqueueSnackbar(data.message || '', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            }
        });
        if (choiceValue?.name?.toLowerCase().includes('approval status')) {
            const reloadPayLoad = {
                limit: perPage,
                offset: pageNumber,
                filterStatus: status,
                order: tableOrder,
                orderId: tableOrderBy,
                searchQuery: searchValue,
                treatment: treatmentData?.toString(),
                startDate,
                endDate,
                contactedCount: contactCount,
                tags: searchTagsValue,
                referral,
                category,
                bookedWith
            };
            updateLeadPage(reloadPayLoad);
            reloadCounter(reloadPayLoad);
        }
        dispatch(followUpSlice(rowData?.lead_id, practiceId, 'LeadDetail', handleClose));
    };

    const handleClose = (data) => handleSetTableData(data);

    const isDateColumn = choiceValue?.name?.toLowerCase().includes('date');

    const attrData = rowData?.leadTreatmentAttributes?.find(el => el.key === choiceValue?.name)?.data?.[0];
    const updatedAt = attrData?.updated_at;
    const tooltipTitle = updatedAt ? `Updated: ${formatUpdatedDate(updatedAt)}` : '';

    if (!rowData?.treatmentData?.[0]?.id) return null;

    // Date column render
    if (isDateColumn) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                bgcolor: dateValue ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                py: 0.5,
            }}>
                <Tooltip title={tooltipTitle} placement="top" arrow disableHoverListener={!tooltipTitle}>
                    <Chip
                        label={dateValue ? moment(dateValue).format('DD/MM/YY') : 'Set date'}
                        size="small"
                        onClick={(e) => setDateAnchor(e.currentTarget)}
                        icon={<Iconify icon="solar:calendar-mark-bold" width={14} />}
                        sx={{
                            height: 22,
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: dateValue ? '#EFF6FF' : '#F3F4F6',
                            color: dateValue ? '#2563EB' : '#9CA3AF',
                            animation: saved ? 'chipSaved 0.3s ease' : 'none',
                            '@keyframes chipSaved': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.15)' },
                                '100%': { transform: 'scale(1)' },
                            },
                            '&:hover': { bgcolor: dateValue ? '#DBEAFE' : '#E5E7EB' },
                            transition: 'all 0.15s',
                        }}
                    />
                </Tooltip>
                <Popover
                    open={Boolean(dateAnchor)}
                    anchorEl={dateAnchor}
                    onClose={() => setDateAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', p: 1 } } }}
                >
                    <DatePicker
                        value={dateValue}
                        format="dd/MM/yyyy"
                        onChange={(val) => onChangeDate(val)}
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    {dateValue && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                            <Chip
                                label="Clear date"
                                size="small"
                                onClick={() => onChangeDate(null)}
                                icon={<Iconify icon="eva:close-circle-fill" width={14} />}
                                sx={{
                                    height: 24,
                                    fontSize: '0.7rem',
                                    cursor: 'pointer',
                                    bgcolor: '#FEE2E2',
                                    color: '#DC2626',
                                    '&:hover': { bgcolor: '#FECACA' },
                                }}
                            />
                        </Box>
                    )}
                </Popover>
            </Box>
        );
    }

    // Select column render (Yes/No/Skip etc.)
    const chipStyle = getChipStyle(value);
    const displayValue = value || '\u2014';

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            bgcolor: value ? `${chipStyle.bgcolor}08` : 'transparent',
            py: 0.5,
        }}>
            <Tooltip title={tooltipTitle} placement="top" arrow disableHoverListener={!tooltipTitle}>
                <Chip
                    label={displayValue}
                    size="small"
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    icon={chipStyle.icon ? <Iconify icon={chipStyle.icon} width={14} /> : undefined}
                    sx={{
                        height: 22,
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: chipStyle.bgcolor,
                        color: chipStyle.color,
                        animation: saved ? 'chipSaved 0.3s ease' : 'none',
                        '@keyframes chipSaved': {
                            '0%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.15)' },
                            '100%': { transform: 'scale(1)' },
                        },
                        '&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
                        transition: 'all 0.15s',
                        '& .MuiChip-icon': { color: 'inherit' },
                    }}
                />
            </Tooltip>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 120, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
            >
                <MenuItem
                    onClick={() => { handleChange({ target: { value: '' } }); setMenuAnchor(null); }}
                    sx={{ fontSize: '0.85rem' }}
                >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#D1D5DB', mr: 1, flexShrink: 0 }} />
                    None
                </MenuItem>
                {choiceValue?.items?.map((el, idx) => {
                    const style = getChipStyle(el.value);
                    return (
                        <MenuItem
                            key={idx}
                            onClick={() => { handleChange({ target: { value: el.value } }); setMenuAnchor(null); }}
                            sx={{ fontWeight: value === el.value ? 700 : 400, fontSize: '0.85rem' }}
                        >
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: style.bgcolor, mr: 1, flexShrink: 0 }} />
                            {el.value}
                            {value === el.value && <Iconify icon="eva:checkmark-fill" width={16} sx={{ ml: 'auto', color: style.bgcolor }} />}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
};

ChoicesField.propTypes = {
    rowData: PropTypes.array,
    tableType: PropTypes.string,
    handleSetTableData: PropTypes.func,
    choiceValue: PropTypes.string,
    updateLeadPage: PropTypes.func,
    reloadCounter: PropTypes.func,
    choicesData: PropTypes.array,
};

export default ChoicesField;
